import { useState, useMemo, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { useParams } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────
interface MeterData {
  id: string;
  name: string;
  area: string;
  lastRead: number;
  prevRead: number;
  lastDate: string;
  period: string;
  petugas: string;
  tarif: string;
  alamat: string;
  telp: string;
  status: string;
  hari: number;
  foto: string | null;
}

interface ApiMeterData {
  nomor: string;
  periode: string;
  nama: string;
  alamat: string;
  gol: string;
  ptgs_met: string;
  nopm: string;
  met_lalu: string;
  met_kini: string;
  pakai: string;
  longitude: string;
  latitude: string;
  lokasi: string;
  status: string;
  versi: string | null;
}

function parseNumber(value: string): number {
  const parsed = parseFloat(String(value).replace(/,/g, "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function mapApiMeterData(apiRow: ApiMeterData): MeterData {
  const prevRead = parseNumber(apiRow.met_lalu);
  const lastRead = parseNumber(apiRow.met_kini);
  const pakai = parseNumber(apiRow.pakai);

  return {
    id: apiRow.nomor,
    name: apiRow.nama,
    area: apiRow.lokasi,
    lastRead,
    prevRead,
    lastDate: "",
    period: apiRow.periode,
    petugas: apiRow.ptgs_met,
    tarif: apiRow.gol,
    alamat: apiRow.alamat,
    telp: "",
    status: apiRow.status,
    hari: 0,
    foto: apiRow.versi || null,
  };
}

// ─── Static Data ──────────────────────────────────────────────────────────────
const DATA: MeterData[] = [];

const PAGE_SIZE_OPTIONS = [1000, 500, 250, 100, 50, 10, 5] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function uniqueSorted(arr: string[]): string[] {
  return Array.from(new Set(arr)).sort();
}

function downloadPDF(data: MeterData[], filename: string = "baca-meter-belum-input.pdf") {
  // Create PDF document
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(16);
  doc.text("Daftar Meter Belum Input", 14, 15);
  
  // Date
  doc.setFontSize(10);
  const now = new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(`Tanggal: ${now}`, 14, 22);
  
  // Table data
  const headers = ["NO. SMB", "Periode", "Nama", "Alamat", "Tarif", "Petugas - Area", "Meter Lalu"];
  const rows = data.map((d) => [
    d.id,
    d.period,
    d.name,
    d.alamat.substring(0, 20) + (d.alamat.length > 20 ? "..." : ""),
    d.tarif,
    (d.petugas + " - " + d.area).substring(0, 20) + ((d.petugas + " - " + d.area).length > 20 ? "..." : ""),
    d.prevRead.toString(),
  ]);

  // Create table using autoTable if available
  if ((doc as any).autoTable) {
    (doc as any).autoTable({
      head: [headers],
      body: rows,
      startY: 30,
      margin: { top: 10, right: 10, bottom: 10, left: 10 },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [29, 78, 216],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 244, 240],
      },
    });
  } else {
    // Fallback: create simple table manually
    const colWidth = 30;
    let yPos = 30;
    
    // Header row
    doc.setFillColor(29, 78, 216);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    
    headers.forEach((header, i) => {
      doc.rect(14 + i * colWidth, yPos, colWidth, 8, "F");
      doc.text(header, 16 + i * colWidth, yPos + 5);
    });
    
    // Data rows
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    yPos += 8;
    
    rows.forEach((row, idx) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      
      if (idx % 2 === 0) {
        doc.setFillColor(245, 244, 240);
        doc.rect(14, yPos, colWidth * headers.length, 7, "F");
      }
      
      row.forEach((cell, i) => {
        doc.text(String(cell), 16 + i * colWidth, yPos + 5);
      });
      
      yPos += 7;
    });
  }

  // Footer with total
  const pageCount = (doc as any).internal.pages.length - 1;
  doc.setFontSize(9);
  doc.text(`Total Data: ${data.length} | Halaman 1 dari ${pageCount}`, 14, doc.internal.pageSize.getHeight() - 10);

  // Download file
  doc.save(filename);
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function Modal({ data, onClose }: { data: MeterData; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ display: "flex", position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", border: "1px solid var(--border-mid)" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "var(--surface)", zIndex: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{data.id}</div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{data.name} · {data.petugas} · {data.period}</div>
          </div>
          <button onClick={onClose} aria-label="Tutup" style={{ width: 30, height: 30, borderRadius: "var(--radius-sm)", background: "transparent", border: "1px solid var(--border)", color: "var(--text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          {/* Foto */}
          
            {data.foto != '1.0.0' ? (
              <div style={{ width: "100%", aspectRatio: "4/3", borderRadius: "var(--radius-md)", overflow: "hidden", background: "var(--surface2)", border: "1px solid var(--border)", marginBottom: 20, position: "relative" }}>
                <img src={data.foto ?? undefined} alt={`Foto meter ${data.id}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
            ) : (
              <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--surface2)", borderRadius: "var(--radius-sm)", padding: "10px 14px", marginTop: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 15, fontWeight: 500 }}> Tidak ada data foto</span>
                </div>
                <span style={{ fontSize: 11, color: "var(--text-hint)", fontFamily: "var(--font-mono)" }}>{data.lastDate}</span>
              </div>
              </div>
            )}

          {/* Info */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid var(--border)" }}>Informasi Pelanggan</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {([["Nama", data.name], ["Area", data.area], ["Alamat", data.alamat], ["Tarif", data.tarif]] as [string, string, boolean?][]).map(([lbl, val, mono]) => (
                <div key={lbl}>
                  <label style={{ display: "block", fontSize: 11, color: "var(--text-hint)", marginBottom: 3 }}>{lbl}</label>
                  <span style={{ fontSize: 14, fontWeight: 500, fontFamily: mono ? "var(--font-mono)" : undefined }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", fontSize: 13, fontWeight: 500, fontFamily: "var(--font-body)", background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border-mid)", borderRadius: "var(--radius-sm)", cursor: "pointer" }}>
            ✕ Tutup
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({
  page, totalPages, pageSize, total, onPage, onPageSize,
}: {
  page: number; totalPages: number; pageSize: PageSize; total: number;
  onPage: (p: number) => void; onPageSize: (s: PageSize) => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const pages: (number | "…")[] = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const arr: (number | "…")[] = [1];
    if (page > 3) arr.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) arr.push(i);
    if (page < totalPages - 2) arr.push("…");
    arr.push(totalPages);
    return arr;
  }, [page, totalPages]);

  const btnBase: React.CSSProperties = {
    minWidth: 32, height: 32, padding: "0 10px", fontSize: 13, fontWeight: 500,
    border: "1px solid var(--border-mid)", borderRadius: "var(--radius-sm)",
    cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontFamily: "var(--font-body)", transition: "background 0.12s",
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 16px", borderTop: "1px solid var(--border)", background: "var(--surface2)", borderRadius: "0 0 var(--radius-lg) var(--radius-lg)" }}>
      {/* Info + page size */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--text-secondary)" }}>
        <span>{from}–{to} dari {total} data</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value) as PageSize)}
          style={{ padding: "4px 8px", fontSize: 12, fontFamily: "var(--font-body)", background: "var(--surface)", border: "1px solid var(--border-mid)", borderRadius: "var(--radius-sm)", color: "var(--text-primary)", cursor: "pointer", outline: "none" }}
        >
          {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s} / hal</option>)}
        </select>
      </div>

      {/* Page buttons */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <button
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
          style={{ ...btnBase, background: page === 1 ? "transparent" : "var(--surface)", color: page === 1 ? "var(--text-hint)" : "var(--text-primary)", cursor: page === 1 ? "default" : "pointer" }}
          aria-label="Halaman sebelumnya"
        >
          ‹
        </button>

        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} style={{ ...btnBase, border: "none", background: "transparent", color: "var(--text-hint)", cursor: "default" }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPage(p as number)}
              style={{ ...btnBase, background: p === page ? "var(--accent)" : "var(--surface)", color: p === page ? "#fff" : "var(--text-primary)", borderColor: p === page ? "var(--accent)" : "var(--border-mid)" }}
              aria-label={`Halaman ${p}`}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}

        <button
          disabled={page === totalPages || totalPages === 0}
          onClick={() => onPage(page + 1)}
          style={{ ...btnBase, background: (page === totalPages || totalPages === 0) ? "transparent" : "var(--surface)", color: (page === totalPages || totalPages === 0) ? "var(--text-hint)" : "var(--text-primary)", cursor: (page === totalPages || totalPages === 0) ? "default" : "pointer" }}
          aria-label="Halaman berikutnya"
        >
          ›
        </button>
      </div>
    </div>
  );
}

async function fetchBacaMeterPotensial(url: string): Promise<{ mapped: MeterData[]; raw: any } | { status: number; message: string; error?: unknown }> {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", "" + process.env.REACT_APP_API_KEY);
    const requestOptions = { method: "GET", headers: myHeaders, redirect: "follow" as RequestRedirect };

    try {
      const response = await fetch(url, requestOptions);
      const result = await response.json();
      let raw: any = result;
      let rows: any[] = [];
      if (Array.isArray(result)) {
        rows = result;
      } else if (result && typeof result === "object" && "data" in result && Array.isArray((result as any).data)) {
        rows = (result as any).data;
        raw = result;
      } else {
        return { status: response.status || 500, message: "Response API tidak dalam format array", error: result };
      }

      const mapped = rows.map((item) => mapApiMeterData(item as ApiMeterData));
      return { mapped, raw };
    } catch (error) {
      return { status: 500, message: "Internal Server Error", error };
    }
}

async function fetchDataPetugasMeter(url : string) {
  const myHeaders = new Headers();
    myHeaders.append("Authorization", "" + process.env.REACT_APP_API_KEY);
    const requestOptions = { method: "GET", headers: myHeaders, redirect: "follow" as RequestRedirect };

    try {
      const response = await fetch(url, requestOptions);
      const result = await response.json();
      let raw: any = result;
      let rows: any[] = [];
      if (Array.isArray(result)) {
        rows = result;
      } else if (result && typeof result === "object" && "data" in result && Array.isArray((result as any).data)) {
        rows = (result as any).data;
        raw = result;
      } else {
        return { status: response.status || 500, message: "Response API tidak dalam format array", error: result };
      }

      const mapped = rows.map((item) => mapApiMeterData(item as ApiMeterData));
      return { mapped, raw };
    } catch (error) {
      return { status: 500, message: "Internal Server Error", error };
    }
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function DashboardBacaMeterPotensial() {
  const [search, setSearch] = useState("");
  const [filterPetugas, setFilterPetugas] = useState("");
  const [filterPeriode, setFilterPeriode] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(1000);
  const [remoteData, setRemoteData] = useState<MeterData[]>([]);
  const [ptgsMet, setPtgsMet] = useState<any>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => (localStorage.getItem('theme') as 'light' | 'dark') || 'light');

  const { ptgs_met } = useParams<{ ptgs_met: string }>();

  const now = new Date();
  const updateTime =
    now.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) +
    " " +
    now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });

  // ── Derived select options ─────────────────────────────────────────────────
  const dataSource = hasFetched ? remoteData : DATA;

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return dataSource.filter(
      (d) =>
        (!q            || d.id.toLowerCase().includes(q) || d.name.toLowerCase().includes(q)) &&
        (!filterPetugas || d.petugas === filterPetugas) &&
        (!filterPeriode || d.period  === filterPeriode) &&
        (!filterArea    || d.area    === filterArea)
    );
  }, [search, filterPetugas, filterPeriode, filterArea, dataSource]);
  // Reset to page 1 on filter change
  useEffect(() => { setPage(1); }, [search, filterPetugas, filterPeriode, filterArea, pageSize]);

  // Fetch data once on mount
  useEffect(() => {
    setIsLoading(true);
    setFetchError(null);
    // const baseUrl = `${process.env.REACT_APP_API_URL_V2}/hublang/baca-meter/belum-input`;
    const baseUrl = `${process.env.REACT_APP_API_URL_V2}/hublang/baca-meter/potensial/${ptgs_met}`;
    const ptgsMetUrl = `${process.env.REACT_APP_API_URL_V2}/hublang/baca-meter/ptgsmet/${ptgs_met}`;
    
    fetchBacaMeterPotensial(baseUrl)
      .then((result) => {
        if (result && typeof result === 'object' && 'mapped' in result) {
          setRemoteData(result.mapped || []);
          setRawResponse(result.raw ?? null);
          setFetchError(null);
        } else if (Array.isArray(result)) {
          setRemoteData(result);
          setRawResponse(result);
          setFetchError(null);
        } else if (result && typeof result === "object" && "status" in result) {
          setFetchError(result.message || "Fetch gagal");
          setRemoteData([]);
          setRawResponse(null);
        } else {
          setFetchError("Response fetch tidak valid");
          setRemoteData([]);
          setRawResponse(null);
        }
      })
      .catch((error) => {
        setFetchError(error?.message ?? String(error));
        setRemoteData([]);
        setRawResponse(null);
      })
      .finally(() => {
        setHasFetched(true);
        setIsLoading(false);
      });

    fetchDataPetugasMeter(ptgsMetUrl)
      .then((result) => {
        if (result && typeof result === 'object' && 'mapped' in result) {
          setRemoteData(result.mapped || []);
          setPtgsMet(result.raw.data ?? null);
          setFetchError(null);
        } else if (Array.isArray(result)) {
          setRemoteData(result);
          setPtgsMet(result);
          setFetchError(null);
        } else if (result && typeof result === "object" && "status" in result) {
          setFetchError(result.message || "Fetch gagal");
          setRemoteData([]);
          setPtgsMet(null);
        } else {
          setFetchError("Response fetch tidak valid");
          setRemoteData([]);
          setPtgsMet(null);
        }
      })
      .catch((error) => {
        setFetchError(error?.message ?? String(error));
        setRemoteData([]);
        setPtgsMet(null);
      })
      .finally(() => {
        setHasFetched(true);
        setIsLoading(false);
      });
  }, []); // Empty dependency - fetch only once on mount

  // Theme sync
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  // ── Active chips ───────────────────────────────────────────────────────────
  const chips: { label: string; clear: () => void }[] = [];
  if (filterPetugas) chips.push({ label: `Petugas: ${filterPetugas}`, clear: () => setFilterPetugas("") });
  if (filterPeriode) chips.push({ label: `Periode: ${filterPeriode}`, clear: () => setFilterPeriode("") });
  if (filterArea)    chips.push({ label: `Area: ${filterArea}`,       clear: () => setFilterArea("") });
  if (search.trim()) chips.push({ label: `"${search.trim()}"`,        clear: () => setSearch("") });

  const resetAll = useCallback(() => {
    setSearch(""); setFilterPetugas(""); setFilterPeriode(""); setFilterArea("");
  }, []);

  const selectedData = useMemo(() => dataSource.find((d) => d.id === selectedId) ?? null, [dataSource, selectedId]);

  // ── Styles ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* CSS Variables */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --bg: #F5F4F0; --surface: #FFFFFF; --surface2: #EEECEA;
          --border: rgba(0,0,0,0.10); --border-mid: rgba(0,0,0,0.18);
          --text-primary: #1A1917; --text-secondary: #6B6A67; --text-hint: #9E9C99;
          --accent: #1D4ED8; --accent-bg: #EEF2FF; --accent-text: #1E3A8A;
          --warn: #92400E; --warn-bg: #FEF3C7;
          --danger: #991B1B; --danger-bg: #FEE2E2;
          --radius-sm: 6px; --radius-md: 10px; --radius-lg: 16px;
          --font-body: 'Plus Jakarta Sans', sans-serif;
          --font-mono: 'IBM Plex Mono', monospace;
        }
        @media (prefers-color-scheme: dark) {
          :root {
            --bg: #111110; --surface: #1C1B1A; --surface2: #242321;
            --border: rgba(255,255,255,0.09); --border-mid: rgba(255,255,255,0.15);
            --text-primary: #F0EEE8; --text-secondary: #9E9C97; --text-hint: #6B6A65;
            --accent: #6B8CFF; --accent-bg: #1A1F3A; --accent-text: #A5B4FC;
            --warn: #FDE68A; --warn-bg: #2D2209;
            --danger: #FCA5A5; --danger-bg: #2D0A0A;
          }
        }
        :root[data-theme='dark'], [data-theme='dark'] {
          --bg: #111110; --surface: #1C1B1A; --surface2: #242321;
          --border: rgba(255,255,255,0.09); --border-mid: rgba(255,255,255,0.15);
          --text-primary: #F0EEE8; --text-secondary: #9E9C97; --text-hint: #6B6A65;
          --accent: #6B8CFF; --accent-bg: #1A1F3A; --accent-text: #A5B4FC;
          --warn: #FDE68A; --warn-bg: #2D2209;
          --danger: #FCA5A5; --danger-bg: #2D0A0A;
        }
        :root[data-theme='light'], [data-theme='light'] {
          --bg: #F5F4F0; --surface: #FFFFFF; --surface2: #EEECEA;
          --border: rgba(0,0,0,0.10); --border-mid: rgba(0,0,0,0.18);
          --text-primary: #1A1917; --text-secondary: #6B6A67; --text-hint: #9E9C99;
          --accent: #1D4ED8; --accent-bg: #EEF2FF; --accent-text: #1E3A8A;
          --warn: #92400E; --warn-bg: #FEF3C7;
          --danger: #991B1B; --danger-bg: #FEE2E2;
        }
        body { font-family: var(--font-body); background: var(--bg); color: var(--text-primary); min-height: 100vh; font-size: 15px; line-height: 1.6; }
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Plus+Jakarta+Sans:wght@400;500;600&display=swap');
        .bm-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
        .bm-table thead { background: var(--surface2); border-bottom: 1px solid var(--border); }
        .bm-table th { padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap; }
        .bm-table tbody tr { border-bottom: 1px solid var(--border); transition: background 0.12s; }
        .bm-table tbody tr:last-child { border-bottom: none; }
        .bm-table tbody tr:hover { background: var(--surface2); }
        .bm-table td { padding: 12px 16px; vertical-align: middle; }
        .bm-btn-preview { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; font-size: 12px; font-weight: 500; font-family: var(--font-body); background: transparent; border: 1px solid var(--border-mid); border-radius: var(--radius-sm); color: var(--text-primary); cursor: pointer; transition: background 0.12s, border-color 0.12s; }
        .bm-btn-preview:hover { background: var(--accent-bg); border-color: var(--accent); color: var(--accent-text); }
        .bm-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 12px 14px; cursor: pointer; transition: background 0.12s; display: flex; align-items: center; gap: 12px; }
        .bm-card:hover { background: var(--surface2); }
        select:focus, input:focus { outline: none; border-color: var(--accent) !important; }
        @media (max-width: 700px) { .bm-table-wrap { display: none !important; } .bm-cards { display: flex !important; } }
      `}</style>

      {/* Header */}
      <header style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)", padding: "0 24px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, fontSize: 15 }}>
            <div style={{ width: 32, height: 32, background: "var(--accent)", borderRadius: "var(--radius-sm)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}>⚡</div>
            <div>
              Baca Meter
              <div style={{ fontSize: 12, color: "var(--text-hint)", fontWeight: 400 }}>Data Potensial</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 12, color: "var(--text-hint)", fontFamily: "var(--font-mono)" }}>{updateTime}</div>
            <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')} aria-label="Toggle theme" style={{ padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-mid)', background: 'transparent', cursor: 'pointer' }}>
              {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 24px 60px" }}> 

        {/* Toolbar */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 180px", maxWidth: 260 }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-hint)", fontSize: 15, pointerEvents: "none" }}>🔍</span>
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari ID / nama…"
                style={{ width: "100%", padding: "8px 12px 8px 34px", fontSize: 13, fontFamily: "var(--font-body)", background: "var(--surface)", border: "1px solid var(--border-mid)", borderRadius: "var(--radius-sm)", color: "var(--text-primary)" }}
              />
            </div>
            <button onClick={resetAll} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 12px", fontSize: 12, fontWeight: 500, fontFamily: "var(--font-body)", background: "transparent", border: "1px solid var(--border-mid)", borderRadius: "var(--radius-sm)", color: "var(--text-secondary)", cursor: "pointer", whiteSpace: "nowrap" }}>
              ↺ Reset
            </button>
            <button onClick={() => downloadPDF(filtered, `Belum Input Baca Meter ${filterPeriode} ${filterPetugas ? filterPetugas : ''}.pdf`)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 12px", fontSize: 12, fontWeight: 500, fontFamily: "var(--font-body)", background: "transparent", border: "1px solid var(--border-mid)", borderRadius: "var(--radius-sm)", color: "var(--text-secondary)", cursor: "pointer", whiteSpace: "nowrap" }}>
              🔎︎ Cari Data Pelanggan
            </button>
            <button onClick={() => downloadPDF(filtered, `Belum Input Baca Meter ${filterPeriode} ${filterPetugas ? filterPetugas : ''}.pdf`)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 12px", fontSize: 12, fontWeight: 500, fontFamily: "var(--font-body)", background: "transparent", border: "1px solid var(--border-mid)", borderRadius: "var(--radius-sm)", color: "var(--text-secondary)", cursor: "pointer", whiteSpace: "nowrap" }}>
              📄 PDF ({filtered.length})
            </button>
          </div>
        </div>   

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",}}>
          <span style={{ fontSize: 15, fontWeight: 600, flex: "1 1 160px" }}>
            Daftar Meter Potensial
          </span>
        </div> 

        {
          ptgsMet ?
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 15, fontWeight: 400 }}>Nama :  { ptgsMet[0].nama}</span>
            </div>
            
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: "15px" }}>
              <span style={{ fontSize: 15, fontWeight: 400 }}>Petugas Meter : { ptgsMet[0].nik}</span>
            </div>
          </> 
          :
          ""
        }
        

        {/* Filter chips */}
        {chips.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {chips.map((c) => (
              <div key={c.label} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", fontSize: 12, fontWeight: 500, background: "var(--accent-bg)", color: "var(--accent-text)", borderRadius: 100, border: "1px solid rgba(29,78,216,0.2)" }}>
                {c.label}
                <button onClick={c.clear} aria-label="Hapus" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent-text)", fontSize: 13, lineHeight: 1, padding: 0, display: "flex", alignItems: "center", opacity: 0.7 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Desktop table */}
        <div className="bm-table-wrap" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", overflow: "hidden", opacity: isLoading ? 0.6 : 1, pointerEvents: isLoading ? "none" : "auto", transition: "opacity 0.2s" }} key={`table-${filtered.length}-${page}`}>
          
          {(isLoading || fetchError) ? (
            <div style={{ marginBottom: 16, padding: 14, borderRadius: 12, background: fetchError ? "#FEF2F2" : "#EFF6FF", color: fetchError ? "#B91C1C" : "#1D4ED8", border: fetchError ? "1px solid #FECACA" : "1px solid #BFDBFE" }}>
              {isLoading ? "Memuat data Baca Meter..." : `Error: ${fetchError}`}
            </div>
          )
          :
          <>
          <table className="bm-table" key={`inner-table-${remoteData.length}-${filterPeriode}-${filterPetugas}`}>
            <thead>
              <tr>
                {["NO", "NO. SMB", "Info Pelanggan", "Status"].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody key={`tbody-${paged.map(p => p.id).join(',')}`}>
              {paged.length === 0 ? (
                <tr key="empty"><td colSpan={5} style={{ textAlign: "center", padding: "48px 24px", color: "var(--text-secondary)" }}>😶 Tidak ada data yang cocok.</td></tr>
              ) : paged.map((d, idx) => (
                <tr key={`row-${d.id}-${idx}`}>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{idx + 1}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{d.id}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-hint)" }}>{d.alamat} -- {d.tarif}</div>
                  </td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: d.status === '30' ? 'green' : d.status === '20' ? 'orange' : d.status === '40' ? 'red' : '' }}>{ d.status === '30' ? 'Aktif' : d.status === '20' ? 'Putus Sementara' : d.status === '40' ? 'Cabut Meter' : '' }</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        }

          {/* Pagination */}
          <Pagination
            page={page} totalPages={totalPages} pageSize={pageSize}
            total={filtered.length}
            onPage={setPage}
            onPageSize={(s) => setPageSize(s)}
          />
        </div>

        {/* Mobile cards */}
        <div 
          className="bm-cards" 
          style={{ display: "none", flexDirection: "column", gap: 8 }}
          key={`cards-${filtered.length}-${page}`} 
        >
          {paged.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 24px", color: "var(--text-secondary)" }}>😶 Tidak ada data yang cocok.</div>
          ) : paged.map((d, idx) => (
            <div 
              key={`card-${d.id}-${idx}`} 
              className="bm-card"
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.id}</span>
                  <span style={{ fontWeight: 400, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span>{d.alamat}</span>
                  <span>· {d.tarif}</span>
                  <span>· <span style={{ color: d.status === '30' ? 'green' : d.status === '20' ? 'orange' : d.status === '40' ? 'red' : '' }}>{ d.status === '30' ? 'Aktif' : d.status === '20' ? 'Putus Sementara' : d.status === '40' ? 'Cabut Meter' : '' }</span></span>
                </div>
              </div>
            </div>
          ))}

          {/* Mobile pagination */}
          <Pagination
            page={page} totalPages={totalPages} pageSize={pageSize}
            total={filtered.length}
            onPage={setPage}
            onPageSize={(s) => setPageSize(s)}
          />
        </div>

      </main>
    </>
  );
}